import type { PortalRole, Space } from '@product-portal/shared';

export interface NavItem {
  label: string;
  to: string;
  /** Roles that see the entry. An inaccessible entry is not rendered at all (never greyed out). */
  roles: readonly PortalRole[];
  /** Exact match for the active state. */
  end?: boolean;
}

const ALL: readonly PortalRole[] = ['requester', 'referent', 'po'];
const PO_ONLY: readonly PortalRole[] = ['po'];

/** Navigation of each space, from the headers of mock-ups 01, 07 and 10. */
export const NAVIGATION: Record<Space, readonly NavItem[]> = {
  business: [
    { label: 'Accueil', to: '/', roles: ALL, end: true },
    { label: 'Mes demandes', to: '/demandes', roles: ALL },
    { label: 'Produits & projets', to: '/produits', roles: ALL },
    { label: 'Release notes', to: '/release-notes', roles: ALL },
    // Screen 06: Référent (own team, read-only) and PO (all teams).
    { label: 'Mon équipe', to: '/mon-equipe', roles: ['referent', 'po'] },
  ],
  product: [
    { label: 'Qualification', to: '/produit/qualification', roles: PO_ONLY },
    { label: 'Backlog & arbitrage', to: '/produit/backlog', roles: PO_ONLY },
    { label: 'Release notes', to: '/produit/release-notes', roles: PO_ONLY },
    { label: 'Administration', to: '/admin/equipes', roles: PO_ONLY },
  ],
  admin: [
    { label: 'Équipes', to: '/admin/equipes', roles: PO_ONLY },
    { label: 'Utilisateurs & accès', to: '/admin/utilisateurs', roles: PO_ONLY },
    { label: 'Produits', to: '/admin/produits', roles: PO_ONLY },
    { label: "Journal d'audit", to: '/admin/journal', roles: PO_ONLY },
  ],
};

/** Spaces a role may enter. Mirrors the API guards; it is display, not protection. */
export const SPACE_ROLES: Record<Space, readonly PortalRole[]> = {
  business: ALL,
  product: PO_ONLY,
  admin: PO_ONLY,
};

export function navigationFor(space: Space, role: PortalRole): NavItem[] {
  if (!SPACE_ROLES[space].includes(role)) return [];
  return NAVIGATION[space].filter((item) => item.roles.includes(role));
}

/** Links from one space header to another (e.g. « ← Espace produit » in the admin header). */
export interface SpaceLink {
  label: string;
  to: string;
  space: Space;
}

export const SPACE_LINKS: Record<Space, readonly SpaceLink[]> = {
  // Not in mock-up 01: the PO needs a way into the product space (see CLAUDE.md, « Écarts »).
  business: [{ label: 'Espace produit →', to: '/produit/qualification', space: 'product' }],
  // Not in mock-up 07: back to the business space without the browser's back button (Robin Meyssonnier, 2026-10-21).
  product: [{ label: '← Espace demandeur', to: '/', space: 'business' }],
  admin: [{ label: '← Espace produit', to: '/produit/qualification', space: 'product' }],
};

export function spaceLinksFor(space: Space, role: PortalRole): SpaceLink[] {
  return SPACE_LINKS[space].filter((link) => SPACE_ROLES[link.space].includes(role));
}
